import * as Api from '@ViewModels';
import moment from 'moment';
import { ImpersonationContextViewModel } from '../../viewmodels/AdminViewModels';
import { InternalAccountIds, InternalLevitateAccountIds } from '../index';

/**
 * Checks if user can view content calendar and campaigns
 * @source Original location: UiUtils.ts.backup line 92
 */
export const canViewContentCalendarAndCampaigns = (
	userSession: Api.UserSessionContext,
	impersonationContext?: Api.IImpersonationContext
) => {
	const planDetails = impersonationContext?.account?.planDetails ?? userSession?.account?.planDetails;
	// Default to not paid if planId is undefined or null. 1 is business and 1.5 is business plus
	return (planDetails?.planId ?? 0) > 0;
};

/**
 * Checks if user can send HTML newsletters
 * @source Original location: UiUtils.ts.backup line 101
 */
export const canSendHtmlNewsletters = (userSession: Api.UserSessionContext) => {
	return (
		canViewContentCalendarAndCampaigns(userSession) &&
		userSession?.account?.features?.htmlNewsletter?.enabled &&
		userSession?.account?.features?.htmlNewsletter?.isConfigured
	);
};

/**
 * Gets list of industries
 * @source Original location: UiUtils.ts.backup line 1244
 */
export const getIndustries = ({ accountId, brokerDealer }: { accountId?: string; brokerDealer?: string } = {}) => {
	let industries = [
		Api.Industry.Insurance,
		Api.Industry.RealEstate,
		Api.Industry.Financial,
		Api.Industry.Mortgage,
		Api.Industry.Legal,
		Api.Industry.Accounting,
		Api.Industry.NonProfit,
		Api.Industry.HomeMaintenance,
		Api.Industry.Health,
		Api.Industry.Dental,
		Api.Industry.Chiropractic,
		Api.Industry.Others,
	];
	if (accountId && InternalLevitateAccountIds.has(accountId)) {
		industries = [Api.Industry.Levitate, ...industries];
	}
	// Northwestern Mutual restriction: only show Financial industry
	if (brokerDealer === 'Northwestern Mutual') {
		industries = industries.filter(industry => industry === Api.Industry.Financial);
	}
	return industries;
};

/**
 * Checks if account is internal
 * @source Original location: UiUtils.ts.backup line 1262
 */
export const isInternalAccount = (accountId: string) => {
	return InternalAccountIds.has(accountId);
};

/**
 * Checks if template is new
 * @source Original location: UiUtils.ts.backup line 1267
 */
export const isTemplateNew = (template: Api.ITemplate) => {
	if (!template?.creationDate) {
		return false;
	}

	return (
		moment().diff(moment(template?.creationDate), 'days') <= 30 &&
		// Don't show as 'New' if they created it themselves or scope is unknown
		(template?.scope === Api.TemplateScope.Industry || template?.scope === Api.TemplateScope.Account)
	);
};

export const accountHasComplianceEnabled = (
	userSession: Api.UserSessionContext,
	impersonationCtx?: ImpersonationContextViewModel
) => {
	return impersonationCtx?.isValid
		? impersonationCtx.account?.preferences?.complianceSettings?.enabled
		: userSession?.account?.preferences?.complianceSettings?.enabled;
};

export const accountHasComplianceRequired = (
	userSession: Api.UserSessionContext,
	impersonationCtx?: ImpersonationContextViewModel
) => {
	return impersonationCtx?.isValid
		? impersonationCtx.account?.preferences?.complianceSettings?.required
		: userSession?.account?.preferences?.complianceSettings?.required;
};

export const getAccountComplianceEmail = (
	userSession: Api.UserSessionContext,
	impersonationCtx?: ImpersonationContextViewModel
) => {
	return impersonationCtx?.isValid
		? impersonationCtx.account?.preferences?.complianceSettings?.emailAddress
		: userSession?.account?.preferences?.complianceSettings?.emailAddress;
};

export const getComplianceDisabledDays = (
	user: Api.UserSessionContext,
	impersonationCtx: ImpersonationContextViewModel,
	ignoreCompliance: boolean
) => {
	const complianceWaitTime = impersonationCtx?.isValid
		? impersonationCtx?.account?.preferences?.complianceSettings?.waitTime
		: user?.account?.preferences?.complianceSettings?.waitTime;
	const mins =
		accountHasComplianceEnabled(user, impersonationCtx) && !ignoreCompliance ? (complianceWaitTime ?? 2880) : 0;
	return moment().add(mins, 'minutes').toDate();
};

export const getComplianceWaitTimeString = (
	user: Api.UserSessionContext,
	impersonationCtx: ImpersonationContextViewModel
) => {
	const days = !impersonationCtx?.isValid
		? convertDurationFromString(user?.account?.preferences?.complianceSettings?.waitTime).days
		: convertDurationFromString(impersonationCtx?.account?.preferences?.complianceSettings?.waitTime).days;
	switch (days) {
		case 1: {
			return '1 day';
		}
		case 2: {
			return '2 days';
		}
		default: {
			return '1 hour';
		}
	}
};

export const getSendOnBehalfWaitTimeString = (
	user: Api.UserSessionContext,
	impersonationCtx: ImpersonationContextViewModel
) => {
	const days = !impersonationCtx?.isValid
		? convertDurationFromString(user?.account?.preferences?.sendOnBehalfWaitTime).days
		: convertDurationFromString(impersonationCtx?.account?.preferences?.sendOnBehalfWaitTime).days;
	let stringTime = '1 hour';
	if (days) {
		stringTime = `${days} day${days > 1 ? 's' : ''}`;
	}
	return stringTime;
};

export const getSendOnBehalfWaitTime = (
	user: Api.UserSessionContext,
	impersonationCtx?: ImpersonationContextViewModel
) => {
	const mins = !impersonationCtx?.isValid
		? user?.account?.preferences?.sendOnBehalfWaitTime
		: impersonationCtx?.account?.preferences?.sendOnBehalfWaitTime;
	return moment().add(mins, 'm');
};

export const getSendOnBehalfDisabledDays = (
	user: Api.UserSessionContext,
	impersonationCtx?: ImpersonationContextViewModel
) => {
	const DEFAULT_DAYS = 2880;
	const mins = !impersonationCtx?.isValid
		? (user?.account?.preferences?.sendOnBehalfWaitTime ?? DEFAULT_DAYS)
		: (impersonationCtx?.account?.preferences?.sendOnBehalfWaitTime ?? DEFAULT_DAYS);
	return moment().add(mins, 'minutes').toDate();
};

export const isExemptFromRegistration = (accountId: string) => {
	const registrationExemptAccounts: string[] = [];
	return registrationExemptAccounts.includes(accountId);
};

export const getValidAllowLevitateImpersonationUntil = (account: Api.IAccount) => {
	if (!account?.allowLevitateImpersonationUntil) {
		return null;
	}

	const impersonationUntilIsStillValid = moment().isBefore(account.allowLevitateImpersonationUntil);
	return impersonationUntilIsStillValid ? account.allowLevitateImpersonationUntil : null;
};

// Helper function for duration conversion (needed by compliance functions)
const convertDurationFromString = (durationString: string | undefined) => {
	if (!durationString) {
		return null;
	}
	let rest = durationString;

	const parsed: Api.IMeetingDuration = {
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	};

	if (durationString.includes('.')) {
		const [days, r] = durationString.split('.');
		parsed.days = parseInt(days, 10);
		rest = r;
	}

	const [h, m, s] = rest.split(':');
	return {
		...parsed,
		hours: parseInt(h, 10),
		minutes: parseInt(m, 10),
		seconds: parseInt(s, 10),
	};
};

export function isAccountFinancialReviewEnabled(accountPreferences: Api.IAccountPreferences) {
	return !!accountPreferences.resourceSelectorSettings?.FinancialReview?.daysToLookAhead;
}
